<svg
    class="seg-svg sankey-svg"
    [class.panning]="panning()"
    [attr.viewBox]="'0 0 ' + layout().width + ' ' + layout().height"
    [attr.width]="layout().width"
    [attr.height]="layout().height"
    preserveAspectRatio="xMidYMin meet"
    role="img"
    [attr.aria-label]="ariaLabel()"
    (wheel)="onWheel($event)"
    (pointerdown)="onPointerDown($event)"
    (pointermove)="onPointerMove($event)"
    (pointerup)="onPointerUp($event)"
    (pointercancel)="onPointerUp($event)"
    (dblclick)="onResetView()"
>
    <!-- reusable fills: a diagonal hatch (used for the "partial" coverage state) -->
    <defs>
        <pattern id="fm-hatch-partial" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <rect class="fm-hatch-bg" width="6" height="6" />
            <line class="fm-hatch-line" x1="0" y1="0" x2="0" y2="6" />
        </pattern>
    </defs>

    <!-- background: click empty space to clear hover + notify consumer -->
    <rect class="graph-bg" x="0" y="0" [attr.width]="layout().width" [attr.height]="layout().height" (click)="onBackground()" />

    <!-- zoom/pan layer: everything the user zooms + pans rides this one transform; the background above
         stays fixed so empty-space clicks and pan-anywhere still work -->
    <g class="zoom-layer" [attr.transform]="transform()">
    <!-- column headers: centered over each node column, in the top band -->
    <g class="sankey-headers">
        @for (hd of layout().headers; track hd.text) {
            <text class="sankey-col-header" [attr.x]="hd.x" [attr.y]="hd.y" [attr.font-size]="headerFontSize()" text-anchor="middle">{{ hd.text }}</text>
        }
    </g>

    <!-- ribbons first (under nodes) -->
    <g class="sankey-links">
        @for (l of layout().links; track l.key) {
            <path
                [class]="linkClass(l)"
                [class.sankey-dim]="isLinkDim(l)"
                [attr.d]="l.path"
                [style.stroke-width.px]="l.width"
                role="button"
                tabindex="0"
                [attr.aria-label]="l.ariaLabel"
                (click)="onLinkClick(l, $event)"
                (keydown.enter)="onLinkClick(l)"
                (keydown.space)="$event.preventDefault(); onLinkClick(l)"
            />
        }
    </g>

    <!-- Proportional selection overlay: a brand-blue band sized to the selected share of each ribbon, drawn
         over the base ribbon so the rest of it keeps its coverage colour. -->
    <g class="sankey-emphasis-links" aria-hidden="true">
        @for (l of layout().links; track l.key) {
            @if (isLinkEmphasis(l)) {
                <path class="sankey-emphasis-ribbon" [attr.d]="l.path" [style.stroke-width.px]="emphasisWidth(l)" />
            }
        }
    </g>

    <!-- Proportional hover overlay: same proportional band for the hovered path, drawn on top. -->
    <g class="sankey-hover-links" aria-hidden="true">
        @for (l of layout().links; track l.key) {
            @if (isLinkHover(l)) {
                <path class="sankey-hover-ribbon" [attr.d]="l.path" [style.stroke-width.px]="hoverWidth(l)" />
            }
        }
    </g>

    <!-- Optional service names drawn on the ribbons (endpoint-ward links carry the label). Dimmed ribbons
         drop their label so a highlighted path is not buried; a halo keeps the text readable over the ribbon. -->
    @if (showServices()) {
        <g class="sankey-svc-labels" aria-hidden="true">
            @for (l of layout().links; track l.key) {
                <!-- Only ribbons thick enough to seat the text get a label, so thin converging ribbons do not
                     stack their labels into an unreadable pile. -->
                @if (l.serviceLabel && !isLinkDim(l) && l.width >= svcLabelMinWidth()) {
                    <text class="sankey-svc-label" [attr.x]="l.labelX" [attr.y]="l.labelY"
                        [attr.font-size]="headerFontSize()" [attr.stroke-width]="labelHaloWidth()"
                        text-anchor="middle" dominant-baseline="middle">{{ l.serviceLabel }}</text>
                }
            }
        </g>
    }

    <!-- nodes -->
    <g class="sankey-nodes">
        @for (n of layout().nodes; track n.id) {
            <g
                [class]="nodeGroupClass(n)"
                [class.sankey-dim]="isNodeDim(n)"
                [class.sankey-hover]="isNodeHover(n)"
                [class.sankey-emphasis]="isNodeEmphasis(n)"
                [attr.role]="'button'"
                [attr.tabindex]="0"
                [attr.aria-label]="n.ariaLabel"
                (mouseenter)="onNodeHover(n)"
                (mouseleave)="nodeHover.emit(null)"
                (click)="onNodeClick(n, $event)"
                (keydown.enter)="onNodeClick(n)"
                (keydown.space)="$event.preventDefault(); onNodeClick(n)"
            >
                <title>{{ n.title }}</title>
                <rect class="sankey-node" [attr.x]="n.x0" [attr.y]="n.y0" [attr.width]="n.w" [attr.height]="n.h" rx="2" />
                <!-- labels: expandable column shows its name + an expand pill on the left; otherwise first
                     column outside-left, last column outside-right, interior beside -->
                @if (n.expandable) {
                    <text class="sankey-label" [attr.x]="expandLabelX(n)" [attr.y]="n.y0 + n.h / 2" [attr.font-size]="labelFontSize()" [attr.stroke-width]="labelHaloWidth()" text-anchor="end" dominant-baseline="middle">
                        {{ truncLabel(n.label) }}
                    </text>
                    <g
                        class="expand-pill"
                        [class.open]="n.expanded"
                        role="button"
                        [attr.tabindex]="0"
                        [attr.aria-label]="(n.expanded ? 'Collapse ' : 'Expand ') + n.label"
                        (click)="onToggle(n, $event)"
                        (keydown.enter)="onToggle(n, $event)"
                        (keydown.space)="$event.preventDefault(); onToggle(n, $event)"
                    >
                        <rect class="pill-bg" [attr.x]="pillRectX(n)" [attr.y]="pillRectY(n)" [attr.width]="pillW" [attr.height]="pillH" [attr.rx]="pillH / 2" />
                        <polyline class="pill-chev" [attr.points]="chevronPoints(n)" />
                        @if (!n.expanded) {
                            <text class="pill-txt" [attr.x]="pillTextX(n)" [attr.y]="n.y0 + n.h / 2" [attr.font-size]="pillTextSize()" text-anchor="middle" dominant-baseline="central">
                                {{ n.expandCount }}
                            </text>
                        }
                    </g>
                } @else if (n.isFirst) {
                    <text class="sankey-label" [attr.x]="n.x0 - 8" [attr.y]="n.y0 + n.h / 2" [attr.font-size]="labelFontSize()" [attr.stroke-width]="labelHaloWidth()" text-anchor="end" dominant-baseline="middle">
                        {{ truncLabel(n.label) }}
                    </text>
                } @else if (n.isLast) {
                    @if (showServices() && n.serviceLabelShown) {
                        <!-- Two lines: the address, then its service(s) once, below - a quieter second line. -->
                        <text class="sankey-label" [attr.x]="n.x0 + n.w + 8" [attr.y]="n.y0 + n.h / 2 - labelFontSize() * 0.48" [attr.font-size]="labelFontSize()" [attr.stroke-width]="labelHaloWidth()" text-anchor="start" dominant-baseline="middle">
                            {{ truncLabel(n.label) }}
                        </text>
                        <text class="sankey-node-svc" [attr.x]="n.x0 + n.w + 8" [attr.y]="n.y0 + n.h / 2 + labelFontSize() * 0.56" [attr.font-size]="subLabelFontSize()" [attr.stroke-width]="labelHaloWidth()" text-anchor="start" dominant-baseline="middle">
                            {{ n.serviceLabelShown }}
                        </text>
                    } @else {
                        <text class="sankey-label" [attr.x]="n.x0 + n.w + 8" [attr.y]="n.y0 + n.h / 2" [attr.font-size]="labelFontSize()" [attr.stroke-width]="labelHaloWidth()" text-anchor="start" dominant-baseline="middle">
                            {{ truncLabel(n.label) }}
                        </text>
                    }
                } @else {
                    <text class="sankey-label" [attr.x]="n.x0 + n.w + 6" [attr.y]="n.y0 + n.h / 2" [attr.font-size]="labelFontSize()" [attr.stroke-width]="labelHaloWidth()" text-anchor="start" dominant-baseline="middle">
                        {{ truncLabel(n.label, 32) }}
                    </text>
                }
            </g>
        }
    </g>
    </g>
</svg>
